"use client"

import type React from "react"
import { createContext, useContext, useState, useEffect, useRef } from "react"
import type { UserPreferences, UnitSystem, Currency, TemperatureUnit } from "@/lib/unit-conversions"
import { detectUserDefaults, detectUserDefaultsFromIP } from "@/lib/locale-detection"

interface PreferencesContextType {
  preferences: UserPreferences
  setUnitSystem: (system: UnitSystem) => void
  setCurrency: (currency: Currency) => void
  setTemperatureUnit: (unit: TemperatureUnit) => void
  resetToAutoDetect: () => void
}

const PreferencesContext = createContext<PreferencesContextType | undefined>(undefined)

const DEFAULT_PREFERENCES: UserPreferences = {
  unitSystem: "metric",
  currency: "EUR",
  temperatureUnit: "celsius",
}

export function PreferencesProvider({ children }: { children: React.ReactNode }) {
  const [preferences, setPreferences] = useState<UserPreferences>(DEFAULT_PREFERENCES)
  const [isLoaded, setIsLoaded] = useState(false)
  const loadingRef = useRef(false) // Prevent concurrent loads (React Strict Mode)

  useEffect(() => {
    const loadPreferences = async () => {
      // Prevent concurrent calls (React Strict Mode calls effects twice)
      if (loadingRef.current) {
        console.log("Preferences already loading, skipping duplicate call")
        return
      }
      loadingRef.current = true
      const stored = localStorage.getItem("surf-preferences")
      const lastDetection = localStorage.getItem("surf-preferences-last-detection")
      console.log("Stored preferences:", stored)
      console.log("Last detection:", lastDetection)
      
      // Check if we have recent stored preferences (within 24 hours)
      // If so, skip IP detection to avoid unnecessary API calls
      const hasRecentPreferences = stored && lastDetection && 
        (Date.now() - parseInt(lastDetection)) < (24 * 60 * 60 * 1000)
      
      let detected: CountryDefaults
      
      if (hasRecentPreferences) {
        // Use stored preferences directly - no need to call IP API
        console.log("Using stored preferences (recent), skipping IP detection")
        try {
          const parsed = JSON.parse(stored)
          detected = {
            currency: parsed.currency || "USD",
            unitSystem: parsed.unitSystem || "metric",
            temperatureUnit: parsed.temperatureUnit || "celsius",
          }
        } catch {
          // If parsing fails, fall back to browser locale
          detected = detectUserDefaults()
        }
      } else {
        // Only run IP detection if we don't have recent stored preferences
        try {
          console.log("Running IP-based detection...")
          detected = await detectUserDefaultsFromIP()
          console.log("IP-detected preferences:", detected)
        } catch (error) {
          console.log("IP detection failed, falling back to browser locale")
          detected = detectUserDefaults()
          console.log("Browser locale detection:", detected)
        }
      }
      
      // Check if we have stored preferences and if they're recent (within 24 hours)
      const shouldUseStored = stored && lastDetection && 
        (Date.now() - parseInt(lastDetection)) < (24 * 60 * 60 * 1000)
      
      if (shouldUseStored) {
        try {
          const parsed = JSON.parse(stored)
          if (!parsed.temperatureUnit) {
            parsed.temperatureUnit = "celsius"
          }
          
          // Check if stored preferences match current IP detection
          const matchesCurrentLocation = 
            parsed.unitSystem === detected.unitSystem &&
            parsed.currency === detected.currency &&
            parsed.temperatureUnit === detected.temperatureUnit
          
          if (matchesCurrentLocation) {
            console.log("Stored preferences match current location, using stored")
            setPreferences(parsed)
          } else {
            console.log("Stored preferences don't match current location, using IP-detected")
            const ipPreferences: UserPreferences = {
              unitSystem: detected.unitSystem,
              currency: detected.currency,
              temperatureUnit: detected.temperatureUnit,
            }
            setPreferences(ipPreferences)
            // Update localStorage with IP-detected preferences
            localStorage.setItem("surf-preferences", JSON.stringify(ipPreferences))
            localStorage.setItem("surf-preferences-last-detection", Date.now().toString())
          }
        } catch (error) {
          console.error("Failed to parse stored preferences:", error)
          // Fall back to IP detection if parsing fails
          const ipPreferences: UserPreferences = {
            unitSystem: detected.unitSystem,
            currency: detected.currency,
            temperatureUnit: detected.temperatureUnit,
          }
          setPreferences(ipPreferences)
          localStorage.setItem("surf-preferences", JSON.stringify(ipPreferences))
          localStorage.setItem("surf-preferences-last-detection", Date.now().toString())
          console.log("Fallback to IP-detected preferences:", ipPreferences)
        }
      } else {
        // No stored preferences or they're old, use IP detection
        const ipPreferences: UserPreferences = {
          unitSystem: detected.unitSystem,
          currency: detected.currency,
          temperatureUnit: detected.temperatureUnit,
        }
        setPreferences(ipPreferences)
        localStorage.setItem("surf-preferences", JSON.stringify(ipPreferences))
        localStorage.setItem("surf-preferences-last-detection", Date.now().toString())
        console.log("Using IP-detected preferences:", ipPreferences)
      }
      setIsLoaded(true)
      loadingRef.current = false // Reset after completion
    }
    
    loadPreferences()
  }, [])

  useEffect(() => {
    if (isLoaded) {
      localStorage.setItem("surf-preferences", JSON.stringify(preferences))
    }
  }, [preferences, isLoaded])

  const setUnitSystem = (system: UnitSystem) => {
    setPreferences((prev) => ({ ...prev, unitSystem: system }))
  }

  const setCurrency = (currency: Currency) => {
    setPreferences((prev) => ({ ...prev, currency }))
  }

  const setTemperatureUnit = (unit: TemperatureUnit) => {
    setPreferences((prev) => ({ ...prev, temperatureUnit: unit }))
  }

  const resetToAutoDetect = async () => {
    console.log("Resetting to auto-detected preferences")
    localStorage.removeItem("surf-preferences")
    localStorage.removeItem("surf-preferences-last-detection")
    
    try {
      console.log("Running fresh IP-based detection...")
      const detected = await detectUserDefaultsFromIP()
      const autoPreferences: UserPreferences = {
        unitSystem: detected.unitSystem,
        currency: detected.currency,
        temperatureUnit: detected.temperatureUnit,
      }
      setPreferences(autoPreferences)
      localStorage.setItem("surf-preferences", JSON.stringify(autoPreferences))
      localStorage.setItem("surf-preferences-last-detection", Date.now().toString())
      console.log("Reset to IP-detected preferences:", autoPreferences)
    } catch (error) {
      console.log("IP detection failed, using browser locale")
      const detected = detectUserDefaults()
      const autoPreferences: UserPreferences = {
        unitSystem: detected.unitSystem,
        currency: detected.currency,
        temperatureUnit: detected.temperatureUnit,
      }
      setPreferences(autoPreferences)
      localStorage.setItem("surf-preferences", JSON.stringify(autoPreferences))
      localStorage.setItem("surf-preferences-last-detection", Date.now().toString())
      console.log("Reset to browser-detected preferences:", autoPreferences)
    }
  }

  return (
    <PreferencesContext.Provider value={{ preferences, setUnitSystem, setCurrency, setTemperatureUnit, resetToAutoDetect }}>
      {children}
    </PreferencesContext.Provider>
  )
}

export function usePreferences() {
  const context = useContext(PreferencesContext)
  if (!context) {
    throw new Error("usePreferences must be used within PreferencesProvider")
  }
  return context
}
